import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, WATCHLIST_ONBOARDING_PATH } from '../../context/AuthContext';

// Routes a user can be on without being pulled into onboarding.
const EXEMPT = /^\/(onboarding|enter-dob|verify|verify-email|reset-password|login|register|admin|settings|delete-account|quiz)(\/|$)/;

/**
 * Server-side onboarding rule enforcement on session restore: if the stored
 * session says onboarding is still needed (onboarding_completed_at IS NULL and
 * no coin yet), a fresh load of Home goes to /onboarding instead; a pending
 * post-first-coin Watchlist step goes to /onboarding/watchlist. The status
 * is re-fetched once per load so completion persists across restarts/devices.
 */
export default function OnboardingGate() {
    const { user, loading, refreshOnboardingStatus } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const checkedRef = useRef(false);

    useEffect(() => {
        if (loading || !user || checkedRef.current) return;
        checkedRef.current = true;
        (async () => {
            const status = (await refreshOnboardingStatus()) || { needsOnboarding: user.needsOnboarding, requiresDob: !user.dobProvided };
            if (EXEMPT.test(location.pathname)) return;
            if (status.requiresDob) { navigate('/enter-dob', { replace: true, state: { from: location } }); return; }
            if (status.needsOnboarding && location.pathname === '/') { navigate('/onboarding', { replace: true }); return; }
            // Post-first-coin Watchlist step left unfinished (tab closed, other
            // device): offer it once on the next fresh load of Home. The quiz and
            // coin are never replayed.
            if (status.needsWatchlistOnboarding && location.pathname === '/') {
                navigate(WATCHLIST_ONBOARDING_PATH, { replace: true, state: { entry: 'relaunch' } });
            }
        })();
    }, [loading, user, refreshOnboardingStatus, location, navigate]);

    return null;
}
